/**
 * SleepStep component - Step 1 of the 4-step check-in flow.
 * Captures sleep quality, sleep window, night waking, wake feeling, and optional RHR.
 */

import React from 'react';
import { SleepQuality, NightWaking, WakeFeeling } from '../../types';

export interface SleepStepData {
  sleep_quality: SleepQuality;
  sleep_from_time?: string;
  sleep_to_time?: string;
  night_waking?: NightWaking;
  wake_feeling?: WakeFeeling;
  resting_heart_rate?: number;
}

interface SleepStepProps {
  data: SleepStepData;
  onChange: (data: SleepStepData) => void;
}

const SLEEP_QUALITY_OPTIONS: { value: SleepQuality; label: string }[] = [
  { value: 'poor', label: 'Poor' },
  { value: 'fair', label: 'Fair' },
  { value: 'good', label: 'Good' },
  { value: 'great', label: 'Great' },
  { value: 'excellent', label: 'Excellent' },
];

const NIGHT_WAKING_OPTIONS: { value: NightWaking; label: string }[] = [
  { value: 'slept_through', label: 'Slept through' },
  { value: 'woke_once', label: 'Woke once' },
  { value: 'woke_multiple', label: 'Woke multiple times' },
  { value: 'broken_restless', label: 'Broken / restless' },
];

const WAKE_FEELING_OPTIONS: { value: WakeFeeling; label: string }[] = [
  { value: 'refreshed', label: 'Refreshed' },
  { value: 'okay', label: 'Okay' },
  { value: 'tired', label: 'Tired' },
  { value: 'exhausted', label: 'Exhausted' },
];

const SleepStep: React.FC<SleepStepProps> = ({ data, onChange }) => {
  const update = (partial: Partial<SleepStepData>) => {
    onChange({ ...data, ...partial });
  };

  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
        Sleep & Recovery
      </h2>
      <p className="text-neutral-600 mb-6">
        How did you sleep last night?
      </p>

      {/* Sleep Quality */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Sleep quality
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {SLEEP_QUALITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ sleep_quality: option.value })}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-center ${
                data.sleep_quality === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <div className="text-xs font-medium text-neutral-700">{option.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Sleep Window */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Sleep window <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="sleep-from" className="block text-xs text-neutral-500 mb-1">Bedtime</label>
            <input
              id="sleep-from"
              type="time"
              value={data.sleep_from_time || ''}
              onChange={(e) => update({ sleep_from_time: e.target.value || undefined })}
              className="input w-full"
            />
          </div>
          <div>
            <label htmlFor="sleep-to" className="block text-xs text-neutral-500 mb-1">Wake time</label>
            <input
              id="sleep-to"
              type="time"
              value={data.sleep_to_time || ''}
              onChange={(e) => update({ sleep_to_time: e.target.value || undefined })}
              className="input w-full"
            />
          </div>
        </div>
      </div>

      {/* Night Waking */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          Night waking <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {NIGHT_WAKING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ night_waking: option.value })}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                data.night_waking === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Wake Feeling */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-neutral-700 mb-3">
          How did you feel waking up? <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {WAKE_FEELING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => update({ wake_feeling: option.value })}
              className={`p-3 rounded-xl border-2 transition-all duration-200 text-left ${
                data.wake_feeling === option.value
                  ? 'border-primary-600 bg-primary-50 shadow-sm'
                  : 'border-primary-200 bg-white hover:border-primary-400'
              }`}
            >
              <span className="text-sm font-medium text-neutral-700">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Resting Heart Rate */}
      <div>
        <label htmlFor="rhr" className="block text-sm font-medium text-neutral-700 mb-2">
          Resting heart rate <span className="text-neutral-400 font-normal">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            id="rhr"
            type="number"
            value={data.resting_heart_rate ?? ''}
            onChange={(e) => update({
              resting_heart_rate: e.target.value ? parseInt(e.target.value) : undefined
            })}
            className="input w-32"
            placeholder="e.g. 62"
            min="30"
            max="220"
          />
          <span className="text-sm text-neutral-500">bpm</span>
        </div>
      </div>
    </div>
  );
};

export default SleepStep;
